'use client';

import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';
import { MinusIcon } from '@app/app/_icons/MinusIcon';
import { PlusIcon } from '@app/app/_icons/PlusIcon';
import { SyncIcon } from '@app/app/_icons/SyncIcon';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';

const supportedFontSizes = ['normal', 'large', 'extra-large', 'huge'];

export function FontSizeConfigurator() {
    const t = useTranslations();

    const [currentFontSize, setCurrentFontSize] = useState<number>(() => {
        const storedFontSize = supportedFontSizes.indexOf(localStorage.getItem('font-size') ?? '');

        return storedFontSize === -1 ? 0 : storedFontSize;
    });

    const handleIncreaseClick = useCallback(() => {
        setCurrentFontSize(prevFontSize => {
            if (prevFontSize >= supportedFontSizes.length - 1) {
                return supportedFontSizes.length - 1;
            }

            return prevFontSize + 1;
        });
    }, []);

    const handleDecreaseClick = useCallback(() => {
        setCurrentFontSize(prevFontSize => {
            if (prevFontSize <= 0) {
                return 0;
            }

            return prevFontSize - 1;
        });
    }, []);

    const handleResetClick = useCallback(() => {
        setCurrentFontSize(0);
    }, []);

    useChangeEffect(() => {
        document.body.dataset.accessibilityFontSize = supportedFontSizes[currentFontSize];
        localStorage.setItem('font-size', supportedFontSizes[currentFontSize]);
    }, currentFontSize);

    return (
        <div className="flex flex-col gap-1" aria-label={t('accessibility.fontSize')}>
            <div className="flex items-center justify-between gap-10">
                <div className="grow">{t('accessibility.fontSize')}</div>
                <div className="flex items-stretch">
                    <GhostButton
                        type="button"
                        ariaLabel={t('accessibility.decreaseFontSize')}
                        className="rounded-l-md border border-neutral-300 px-3 py-3"
                        onClick={handleDecreaseClick}>
                        <MinusIcon className="size-4" />
                    </GhostButton>
                    <div className="flex items-center border-y border-neutral-300 px-4 text-neutral-500">
                        {t(`accessibility.fontSizes.${supportedFontSizes[currentFontSize]}`)}
                    </div>
                    <GhostButton
                        type="button"
                        ariaLabel={t('accessibility.increaseFontSize')}
                        className="rounded-r-md border border-neutral-300 px-3 py-3"
                        onClick={handleIncreaseClick}>
                        <PlusIcon className="size-4" />
                    </GhostButton>
                </div>
            </div>
            {currentFontSize !== 0 && (
                <div className="flex justify-end">
                    <GhostButton
                        type="button"
                        className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700"
                        onClick={handleResetClick}>
                        <SyncIcon className="size-3" />
                        {t('accessibility.resetFontSize')}
                    </GhostButton>
                </div>
            )}
        </div>
    );
}
